// subparts/TimetableEntryCard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { GlobalChipSelector, ChipOption } from '../../../../components/GlobalComponents/GlobalChipSelector';
import { GlobalCheckboxes, CheckboxOption } from '../../../../components/GlobalComponents/GlobalCheckboxes';
import { GlobalInputField } from '../../../../components/GlobalComponents/GlobalInputField';
import apiClient from '../../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasOptionalTeacher, formatSlotTypeLabel } from './timetableSlotTypes';
import { useSchoolDetailsStore } from '../../../../stateManagers/hooks/useSchoolDetailsStore';

interface TimetableEntry {
  id: string;
  timeSlotId: string;
  subjectId: string;
  // Holds the real teacher_id (name kept for compatibility with the parent form state).
  teacherAssignmentId: string;
  daysOfWeek: string[];
  roomNumber: string;
}

interface TimetableEntryCardProps {
  entry: TimetableEntry;
  index: number;
  classId: string;
  section: string | null;
  canRemove: boolean;
  timeSlotOptions: ChipOption[];
  subjectOptions: ChipOption[];
  timeSlotsLoading: boolean;
  subjectsLoading: boolean;
  timeSlotsError?: string | null;
  subjectsError?: string | null;
  canShowEntries: boolean;
  loading: boolean;
  occupiedDays?: string[];
  slotType?: string | null;
  /**
   * subject_id → teacher already teaching that subject for this class + section
   * in the selected timetable type. The backend allows only ONE teacher per
   * class + section + subject, so when a subject is in this map the teacher is
   * locked to that person.
   */
  subjectTeacherMap?: Record<string, { id: string; name: string }>;
  onUpdate: (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => void;
  onUpdateDays: (id: string, days: string[]) => void;
  onRemove: (id: string) => void;
}

// value === the teacher's id, so the chip value and the API payload are the same thing.
interface TeacherOption extends ChipOption {
  teacherId: string;
}

const SELECT_ALL_VALUE = '__SELECT_ALL__';
const TEACHER_PAGE_SIZE = 100; // backend caps `limit` at 100
const TEACHER_MAX_PAGES = 20;  // safety stop: 2000 teachers

const getId = (item: any): string => String(item.id || item._id || item.ID || '');

const sortByLabel = (list: TeacherOption[]) => [...list].sort((a, b) => a.label.localeCompare(b.label));

const TimetableEntryCard: React.FC<TimetableEntryCardProps> = ({
  entry,
  index,
  classId,
  canRemove,
  timeSlotOptions,
  subjectOptions,
  timeSlotsLoading,
  subjectsLoading,
  timeSlotsError = null,
  subjectsError = null,
  canShowEntries,
  loading,
  occupiedDays = [],
  slotType = null,
  subjectTeacherMap = {},
  onUpdate,
  onUpdateDays,
  onRemove,
}) => {
  const toast = useRef<Toast>(null);

  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  const currentSessionId = useSchoolDetailsStore(
    (state) => state.schoolDetails?.current_session?.id ?? null
  );

  const noSubject = hasNoSubject(slotType);
  const optionalTeacher = hasOptionalTeacher(slotType);
  // Strict non-teaching: no subject AND no teacher allowed (i.e. 'break')
  const noTeacherAtAll = noSubject && !optionalTeacher;

  // No time slots exist at all for this timetable type — the rest of the
  // entry (subject/teacher/days/room) is meaningless without one, so lock it down.
  const noTimeSlotsAvailable = canShowEntries && !timeSlotsLoading && timeSlotOptions.length === 0;

  const baseDayOptions: CheckboxOption[] = [
    { value: 'Monday',    label: 'Monday',    disabled: occupiedDays.includes('Monday')    },
    { value: 'Tuesday',   label: 'Tuesday',   disabled: occupiedDays.includes('Tuesday')   },
    { value: 'Wednesday', label: 'Wednesday', disabled: occupiedDays.includes('Wednesday') },
    { value: 'Thursday',  label: 'Thursday',  disabled: occupiedDays.includes('Thursday')  },
    { value: 'Friday',    label: 'Friday',    disabled: occupiedDays.includes('Friday')    },
    { value: 'Saturday',  label: 'Saturday',  disabled: occupiedDays.includes('Saturday')  },
  ];

  const selectableDayValues = baseDayOptions.filter((d) => !d.disabled).map((d) => d.value);
  const allDaysSelected =
    selectableDayValues.length > 0 &&
    selectableDayValues.every((d) => entry.daysOfWeek.includes(d));

  // "Select All" rendered as the 7th option in the SAME checkbox list, right after Saturday.
  const dayOptions: CheckboxOption[] = [
    ...baseDayOptions,
    {
      value: SELECT_ALL_VALUE,
      label: 'Select All',
      disabled: loading || noTimeSlotsAvailable || selectableDayValues.length === 0,
    },
  ];

  const checkboxValue = allDaysSelected ? [...entry.daysOfWeek, SELECT_ALL_VALUE] : entry.daysOfWeek;

  const handleDaysChange = (values: string[]) => {
    const toggledSelectAll = values.includes(SELECT_ALL_VALUE) && !allDaysSelected;
    const toggledOffSelectAll = !values.includes(SELECT_ALL_VALUE) && allDaysSelected;

    if (toggledSelectAll) {
      onUpdateDays(entry.id, selectableDayValues);
      return;
    }
    if (toggledOffSelectAll) {
      onUpdateDays(entry.id, []);
      return;
    }
    onUpdateDays(entry.id, values.filter((v) => v !== SELECT_ALL_VALUE));
  };

  /* ══════════════════════════════════════════════════════════════════
     TEACHER LIST
     Teachers are no longer "assigned first". Assignments are generated from
     the timetable when it is set active, so any teacher in the school can be
     picked here. Same source the Assign Teachers screen used:
       GET /api/assigned-teachers/teachers-with-assignments  (page + limit)
     We walk every page so schools with more than 100 teachers are not cut off.
     Optional-teacher slots (power_nap / workbook / activities) keep using
     `teachers-list2`, exactly as before.
  ══════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (noTimeSlotsAvailable || noTeacherAtAll) {
      setTeacherOptions([]);
      return;
    }

    let cancelled = false;

    const loadOptionalSlotTeachers = async () => {
      const response = await apiClient.get('teachers-list2', { params: { page: 1, limit: 100 } });
      if (response.data.status !== 'success') return [];
      return (response.data.data ?? [])
        .map((t: any) => ({ value: getId(t), label: t.name || '', teacherId: getId(t) }))
        .filter((t: TeacherOption) => !!t.value) as TeacherOption[];
    };

    const loadSchoolTeachers = async () => {
      const collected = new Map<string, TeacherOption>();
      let page = 1;
      let totalPages = 1;

      do {
        const response = await apiClient.get('/api/assigned-teachers/teachers-with-assignments', {
          params: {
            page,
            limit: TEACHER_PAGE_SIZE,
            session_id: currentSessionId || undefined,
          },
        });
        if (response.data.status !== 'success') break;

        (response.data.data ?? []).forEach((t: any) => {
          const id = getId(t);
          if (id) collected.set(id, { value: id, label: t.name || '', teacherId: id });
        });

        totalPages = response.data.pagination?.total_pages ?? 1;
        page += 1;
      } while (page <= totalPages && page <= TEACHER_MAX_PAGES);

      return Array.from(collected.values());
    };

    (async () => {
      setTeachersLoading(true);
      try {
        const list = optionalTeacher ? await loadOptionalSlotTeachers() : await loadSchoolTeachers();
        if (!cancelled) setTeacherOptions(sortByLabel(list));
      } catch (error) {
        console.error('Error fetching teachers:', error);
        if (!cancelled) {
          setTeacherOptions([]);
          toast.current?.show({
            severity: 'error',
            summary: 'Teachers',
            detail: 'Could not load the teachers list.',
            life: 4000,
          });
        }
      } finally {
        if (!cancelled) setTeachersLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [noTimeSlotsAvailable, noTeacherAtAll, optionalTeacher, currentSessionId]);

  /* ── One teacher per class + section + subject ────────────────────
     If this subject is already scheduled for this class + section in the
     selected timetable type, the teacher is that person — pre-select and lock. */
  const lockedTeacher =
    !optionalTeacher && !noSubject && entry.subjectId ? subjectTeacherMap[entry.subjectId] : undefined;

  useEffect(() => {
    if (lockedTeacher && entry.teacherAssignmentId !== lockedTeacher.id) {
      onUpdate(entry.id, 'teacherAssignmentId', lockedTeacher.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedTeacher?.id, entry.subjectId]);

  const visibleTeacherOptions: TeacherOption[] = lockedTeacher
    ? [{ value: lockedTeacher.id, label: lockedTeacher.name, teacherId: lockedTeacher.id }]
    : teacherOptions;

  const showTeacherSelector = !noTimeSlotsAvailable && (optionalTeacher || (!noSubject && !!entry.subjectId));

  /* ── Empty-state messages, derived from real state — not guessed ── */

  const timeSlotEmptyMessage = !canShowEntries
    ? 'Complete previous fields first'
    : timeSlotsError
      ? timeSlotsError
      : 'No time slots configured for this timetable type';

  const subjectEmptyMessage = !classId
    ? 'Select class first'
    : subjectsError
      ? subjectsError
      : 'No subjects available for this class';

  const teacherEmptyMessage = !optionalTeacher && !entry.subjectId
    ? 'Select subject first'
    : 'No teachers found in this school';

  return (
    <>
    <Toast ref={toast} position="top-right" />

    <div className="entry-card">
      <div className="entry-number">Entry {index + 1}</div>

      {canRemove && (
        <button
          className="remove-button"
          onClick={() => onRemove(entry.id)}
          disabled={loading}
        >
          <i className="pi pi-times" style={{ fontSize: '11px' }} />
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
        <GlobalChipSelector
          label="Time Slot"
          icon="pi pi-clock"
          value={entry.timeSlotId}
          onChange={(v) => onUpdate(entry.id, 'timeSlotId', v)}
          options={timeSlotOptions}
          required
          loading={timeSlotsLoading}
          disabled={loading || !canShowEntries}
          emptyMessage={timeSlotEmptyMessage}
        />

        {noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            No time slots are configured for this timetable type — add one before filling out the rest of this entry
          </div>
        )}

        {!noSubject && (
          <GlobalChipSelector
            label="Subject"
            icon="pi pi-book"
            value={entry.subjectId}
            onChange={(v) => {
              onUpdate(entry.id, 'subjectId', v);
              // Clear teacher when subject changes — the lock effect above re-selects
              // the existing teacher if this subject is already scheduled.
              onUpdate(entry.id, 'teacherAssignmentId', '');
            }}
            options={subjectOptions}
            required
            loading={subjectsLoading}
            disabled={loading || !classId || noTimeSlotsAvailable}
            emptyMessage={subjectEmptyMessage}
          />
        )}

        {noTeacherAtAll && !noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            This is a {formatSlotTypeLabel(slotType || '')} slot — no subject or teacher needed
          </div>
        )}

        {showTeacherSelector && (
          <GlobalChipSelector
            label={optionalTeacher ? 'Supervising Teacher (optional)' : 'Teacher'}
            icon="pi pi-user"
            value={entry.teacherAssignmentId}
            onChange={(v) => onUpdate(entry.id, 'teacherAssignmentId', v)}
            options={visibleTeacherOptions}
            required={!optionalTeacher}
            loading={teachersLoading}
            disabled={loading || !!lockedTeacher || (visibleTeacherOptions.length === 0 && !optionalTeacher)}
            allowDeselect={!lockedTeacher}
            emptyMessage={teacherEmptyMessage}
          />
        )}

        {lockedTeacher && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            {lockedTeacher.name} already teaches this subject for this class and section in this timetable.
            Only one teacher can teach a subject per class and section.
          </div>
        )}

        <div className="days-checkbox-row">
          <style>{`
            .days-checkbox-row label:has(input[value="${SELECT_ALL_VALUE}"]),
            .days-checkbox-row div:has(> input[value="${SELECT_ALL_VALUE}"]) {
              background: #eff6ff;
              border-radius: 6px;
              padding: 4px 8px;
            }
          `}</style>
          <GlobalCheckboxes
            label="Days of Week"
            options={dayOptions}
            value={checkboxValue}
            onChange={handleDaysChange}
            required
            layout="horizontal"
            gap={16}
            disabled={loading || noTimeSlotsAvailable}
          />
        </div>

        <GlobalInputField
          label="Room Number"
          value={entry.roomNumber}
          onChange={(e) => onUpdate(entry.id, 'roomNumber', e.target.value)}
          placeholder="e.g. 101, Lab-3, Auditorium"
          disabled={loading || noTimeSlotsAvailable}
          icon="pi pi-home"
        />
      </div>
    </div>
    </>
  );
};

export default TimetableEntryCard;